import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { useCan } from '../../stores/session';

type Status = 'open' | 'reviewing' | 'actioned' | 'dismissed';

interface Report {
  id: number;
  reason: string;
  detail: string | null;
  status: Status;
  created_at: string;
  reporter: string | null;
  handled_by: string | null;
  resolution_note: string | null;
}

interface QueueEntry {
  subject_type: 'track' | 'comment' | 'user' | 'collection';
  subject_id: number;
  report_count: number;
  first_reported_at: string;
  last_reported_at: string;
  reasons: string[];
  /** Null when the reported thing has since been deleted. */
  subject: Record<string, unknown> | null;
  reports: Report[];
}

const TABS: { id: Status; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'reviewing', label: 'Reviewing' },
  { id: 'actioned', label: 'Actioned' },
  { id: 'dismissed', label: 'Dismissed' },
];

/**
 * The moderation queue (HANDOFF §6.7).
 *
 * ## One entry per reported thing
 *
 * Twelve people reporting one track is twelve rows in the table and one row
 * here. The count is the strongest signal a queue has, but twelve list items
 * about one track is twelve decisions about one thing and pushes everything
 * else off the page.
 *
 * ## Oldest first
 *
 * A queue read newest-first starves its own bottom, and the report nobody ever
 * got to is reliably the one that mattered. The server orders it; this page does
 * not offer a way to re-sort, because "sort by most reported" is how a
 * brigading campaign gets promoted to the top of a moderator's day.
 */
export function AdminReportsPage() {
  const [status, setStatus] = useState<Status>('open');
  const canResolve = useCan('report.resolve');
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['admin-reports', status],
    queryFn: () => api.get<{ items: QueueEntry[]; open_count: number }>(`/admin/reports?status=${status}`),
  });

  const resolve = useMutation({
    mutationFn: (input: {
      subject_type: string;
      subject_id: number;
      status: Status;
      note?: string | null;
    }) => api.post('/admin/reports/resolve', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin-reports'] }),
  });

  const items = data?.items ?? [];

  return (
    <div>

      <div className="mt-5 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Reports</h1>
        {(data?.open_count ?? 0) > 0 && (
          <span className="meta-caps">{data!.open_count} waiting</span>
        )}
      </div>

      <div role="tablist" className="mt-6 flex flex-wrap gap-1 border-b border-line pb-3">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={status === tab.id}
            data-report-tab={tab.id}
            onClick={() => setStatus(tab.id)}
            className={`rounded-[var(--radius-pill)] px-4 py-2 text-sm transition-colors ${
              status === tab.id ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {resolve.isError && (
        <div className="mt-4">
          <Alert tone="error">
            {resolve.error instanceof ApiError ? resolve.error.message : 'That could not be saved.'}
          </Alert>
        </div>
      )}

      {isPending ? (
        <div className="mt-6 flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">
            {status === 'open' ? 'Nothing waiting. ' : 'Nothing here. '}
            {status === 'open' && 'Reports from across the site land here.'}
          </p>
        </Card>
      ) : (
        <ul className="mt-6 flex flex-col gap-4" data-report-queue>
          {items.map((entry) => (
            <li key={`${entry.subject_type}:${entry.subject_id}`} data-report-entry={entry.subject_id}>
              <Entry
                entry={entry}
                canResolve={canResolve && (status === 'open' || status === 'reviewing')}
                pending={
                  resolve.isPending &&
                  resolve.variables?.subject_id === entry.subject_id &&
                  resolve.variables?.subject_type === entry.subject_type
                }
                onResolve={(next, note) =>
                  resolve.mutate({
                    subject_type: entry.subject_type,
                    subject_id: entry.subject_id,
                    status: next,
                    note,
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Entry({
  entry,
  canResolve,
  pending,
  onResolve,
}: {
  entry: QueueEntry;
  canResolve: boolean;
  pending: boolean;
  onResolve: (status: Status, note: string | null) => void;
}) {
  const [note, setNote] = useState('');
  const subject = entry.subject;

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Chip>{entry.subject_type}</Chip>
            {entry.report_count > 1 && (
              <Chip tone="hot">{entry.report_count} reports</Chip>
            )}
            {entry.reasons.map((reason) => (
              <Chip key={reason}>{reason}</Chip>
            ))}
          </div>

          {/*
            The reported thing itself, not a link to it. A queue that makes you
            open every item to find out what it is about is a queue nobody
            works through.
          */}
          {subject === null ? (
            <p className="mt-2 text-sm italic text-faint" data-subject-gone>
              This has been deleted since it was reported.
            </p>
          ) : (
            <Subject type={entry.subject_type} subject={subject} />
          )}
        </div>

        {subject !== null && typeof subject.href === 'string' && (
          <Link
            to={subject.href}
            className="shrink-0 text-sm text-accent hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            Open ↗
          </Link>
        )}
      </div>

      <details className="border-t border-line pt-3">
        <summary className="cursor-pointer text-sm text-soft hover:text-ink">
          {entry.reports.length === 1 ? 'The report' : `All ${entry.reports.length} reports`}
        </summary>

        <ul className="mt-3 flex flex-col gap-3">
          {entry.reports.map((report) => (
            <li key={report.id} className="border-l border-line pl-3">
              <div className="flex flex-wrap items-center gap-2">
                <Chip>{report.reason}</Chip>
                <span className="meta-caps">
                  {report.reporter ? `@${report.reporter}` : 'a deleted account'}
                </span>
                <span className="meta-caps">{report.created_at.slice(0, 16).replace('T', ' ')}</span>
              </div>
              {report.detail && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-soft">{report.detail}</p>
              )}
              {report.resolution_note && (
                <p className="mt-1 text-xs text-faint">
                  {report.handled_by ? `@${report.handled_by}: ` : ''}
                  {report.resolution_note}
                </p>
              )}
            </li>
          ))}
        </ul>
      </details>

      {canResolve && (
        <div className="flex flex-col gap-3 border-t border-line pt-3">
          <input
            className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-line-strong"
            placeholder="What did you do? Required to action."
            value={note}
            maxLength={2000}
            data-resolution-note
            onChange={(event) => setNote(event.target.value)}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              variant="danger"
              className="px-4 py-1.5"
              loading={pending}
              // The server refuses this without a note; disabling it here means
              // the rule is visible before the click rather than after.
              disabled={note.trim() === ''}
              data-action-report
              onClick={() => onResolve('actioned', note.trim())}
            >
              Actioned
            </Button>
            <Button
              variant="secondary"
              className="px-4 py-1.5"
              loading={pending}
              data-dismiss-report
              onClick={() => onResolve('dismissed', note.trim() || null)}
            >
              Dismiss
            </Button>
            <Button
              variant="ghost"
              className="px-4 py-1.5"
              loading={pending}
              onClick={() => onResolve('reviewing', note.trim() || null)}
            >
              Looking into it
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Subject({ type, subject }: { type: string; subject: Record<string, unknown> }) {
  if (type === 'comment') {
    return (
      <>
        <p className="mt-2 whitespace-pre-wrap rounded-[var(--radius-control)] bg-raised px-3 py-2 text-sm text-ink">
          {String(subject.body ?? '')}
        </p>
        <p className="meta-caps mt-1.5">
          @{String(subject.author ?? '?')}
          {subject.deleted === true && ' · already deleted'}
        </p>
      </>
    );
  }

  if (type === 'user') {
    return (
      <p className="mt-2 text-sm text-ink">
        @{String(subject.username ?? '?')}
        {subject.display_name ? ` — ${String(subject.display_name)}` : ''}
        <span className="meta-caps ml-2">{String(subject.status ?? '')}</span>
      </p>
    );
  }

  return (
    <p className="mt-2 text-sm text-ink">
      {String(subject.title ?? '?')}
      <span className="meta-caps ml-2">
        @{String(subject.owner ?? '?')} · {String(subject.visibility ?? '')}
        {subject.on_hold === true && ' · on hold'}
      </span>
    </p>
  );
}
